/**
 * API Service Layer
 * Centralized service for all API communications with the Laravel backend
 */

import axios from 'axios';
import { getToken, setToken, removeToken } from './tokenManager';
import { authEvents } from './authEvents';

// Base URL for API - should be configured via environment variable
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Create axios instance with base configuration
 */
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Request interceptor
 * Adds authentication token to all requests if available
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles global error responses and token management
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Authentication error - clear token AND redirect to login
          removeToken();
          console.error('Authentication error: Token expired or invalid');
          // Notify React layer to redirect to /login
          authEvents.emitUnauthorized();
          break;

        case 403:
          // Authorization error - access denied
          console.error('Authorization error: Access denied');
          error.userMessage = 'Accès refusé. Vous ne disposez pas des permissions nécessaires.';
          break;

        case 422:
          // Validation error - return field-specific errors
          console.error('Validation error:', data.errors);
          error.validationErrors = data.errors;
          error.userMessage = 'Veuillez corriger les erreurs du formulaire.';
          break;

        case 500:
          // Server error - log and show generic message
          console.error('Server error:', error);
          error.userMessage = 'Une erreur serveur est survenue. Veuillez réessayer.';
          break;

        default:
          console.error('API error:', error);
          error.userMessage = data.message || 'Une erreur est survenue. Veuillez réessayer.';
      }
    } else if (error.request) {
      // Network error - no response received
      console.error('Network error:', error);
      error.userMessage = 'Erreur de connexion. Vérifiez votre connexion internet.';
    } else {
      // Other errors
      console.error('Error:', error.message);
      error.userMessage = 'Une erreur inattendue est survenue.';
    }

    return Promise.reject(error);
  }
);

/**
 * API Service Class
 * Provides methods for all API endpoints
 */
class ApiService {
  // ==================== Authentication Endpoints ====================

  /**
   * Register a new user
   * @param {Object} data - Registration data
   * @param {string} data.first_name - User's first name
   * @param {string} data.last_name - User's last name
   * @param {string} data.email - User's email
   * @param {string} [data.phone] - User's phone number
   * @param {string} data.password - User's password (min 8 characters)
   * @param {string} data.password_confirmation - Password confirmation
   * @param {string} data.role - User's role (parent/tutor)
   * @returns {Promise<Object>} Auth response with user and token
   */
  async register(data) {
    const response = await apiClient.post('/auth/register', data);
    const authData = response.data.data || response.data;
    if (authData.access_token) {
      setToken(authData.access_token);
    }
    return authData;
  }

  /**
   * Login user
   * @param {Object} data - Login credentials
   * @param {string} data.email - User's email
   * @param {string} data.password - User's password
   * @param {string} [data.two_factor_code] - Optional 2FA code (6 digits)
   * @returns {Promise<Object>} Auth response with user and token
   */
  async login(data) {
    const response = await apiClient.post('/auth/login', data);
    const authData = response.data.data || response.data;
    if (authData.access_token) {
      setToken(authData.access_token);
    }
    return authData;
  }

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      removeToken();
    }
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User resource
   */
  async getProfile() {
    const response = await apiClient.get('/auth/profile');
    return response.data.data || response.data;
  }

  /**
   * Update user profile
   * @param {Object} data - Profile update data
   * @returns {Promise<Object>} Updated user resource
   */
  async updateProfile(data) {
    const response = await apiClient.put('/auth/profile', data);
    return response.data.data || response.data;
  }

  // ==================== Two-Factor Authentication Endpoints ====================

  async enable2FA() {
    const response = await apiClient.post('/profile/two-factor-authentication');
    return response.data;
  }

  async confirm2FA(code) {
    const response = await apiClient.post('/profile/two-factor-authentication/confirm', { code });
    return response.data;
  }

  async verify2FA(email, code) {
    const response = await apiClient.post('/auth/two-factor-challenge', { email, code });
    const authData = response.data.data || response.data;
    if (authData.access_token) {
      setToken(authData.access_token);
    }
    return authData;
  }

  async disable2FA() {
    const response = await apiClient.delete('/profile/two-factor-authentication');
    return response.data;
  }

  // ==================== Student Endpoints ====================

  async getStudents(filters = {}) {
    const response = await apiClient.get('/students', { params: filters });
    return response.data;
  }

  async getStudent(id) {
    const response = await apiClient.get(`/students/${id}`);
    return response.data;
  }

  async createStudent(data) {
    const response = await apiClient.post('/students', data);
    return response.data;
  }

  async updateStudent(id, data) {
    const response = await apiClient.put(`/students/${id}`, data);
    return response.data;
  }

  async deleteStudent(id) {
    const response = await apiClient.delete(`/students/${id}`);
    return response.data;
  }

  async uploadStudentPhoto(id, photo) {
    const formData = new FormData();
    formData.append('photo', photo);
    const response = await apiClient.post(`/students/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteStudentPhoto(id) {
    const response = await apiClient.delete(`/students/${id}/photo`);
    return response.data;
  }

  // ==================== Evaluation Endpoints (Part 2) ====================

  /**
   * Get evaluations for a contract
   * GET /contracts/{contractId}/evaluations
   */
  async getEvaluations(contractId, params = {}) {
    const response = await apiClient.get(`/contracts/${contractId}/evaluations`, { params });
    return response.data;
  }

  /**
   * Create or update an evaluation (score entry)
   * POST /contracts/{contractId}/evaluations
   */
  async saveEvaluation(contractId, data) {
    const response = await apiClient.post(`/contracts/${contractId}/evaluations`, data);
    return response.data;
  }

  /**
   * Update a specific evaluation
   * PUT /contracts/{contractId}/evaluations/{id}
   */
  async updateEvaluation(contractId, id, data) {
    const response = await apiClient.put(`/contracts/${contractId}/evaluations/${id}`, data);
    return response.data;
  }

  // ==================== Evaluation Config Endpoints (Part 2) ====================

  /**
   * Get evaluation config for a contract
   * GET /contracts/{contractId}/evaluation-config
   */
  async getEvaluationConfig(contractId) {
    const response = await apiClient.get(`/contracts/${contractId}/evaluation-config`);
    return response.data;
  }

  /**
   * Upsert evaluation config (n_ie, n_ds, ie_weight, ds_weight)
   * POST /contracts/{contractId}/evaluation-config
   */
  async updateEvaluationConfig(contractId, data) {
    const response = await apiClient.post(`/contracts/${contractId}/evaluation-config`, data);
    return response.data;
  }

  // ==================== Bulletin Endpoints (Part 2) ====================

  /**
   * Get bulletin (computed averages) for a student in a period
   * GET /students/{studentId}/bulletin/{periodId}
   */
  async getBulletin(studentId, periodId) {
    const response = await apiClient.get(`/students/${studentId}/bulletin/${periodId}`);
    return response.data;
  }

  // ==================== Period Endpoints (Part 2) ====================

  /**
   * Get all periods (global reference data)
   * GET /periods
   */
  async getPeriods() {
    const response = await apiClient.get('/periods');
    return response.data;
  }

  // ==================== Contract Endpoints ====================

  /**
   * Get contracts (optionally filtered)
   * GET /contracts
   */
  async getContracts(params = {}) {
    const response = await apiClient.get('/contracts', { params });
    return response.data;
  }

  /**
   * Get a single contract
   * GET /contracts/{id}
   */
  async getContract(id) {
    const response = await apiClient.get(`/contracts/${id}`);
    return response.data;
  }

  // ==================== Slot Endpoints (Part 3) ====================

  /**
   * Get slots for a contract
   * GET /contracts/{contractId}/slots
   */
  async getSlots(contractId) {
    const response = await apiClient.get(`/contracts/${contractId}/slots`);
    return response.data;
  }

  /**
   * Create a slot (triggers session generation)
   * POST /contracts/{contractId}/slots
   */
  async createSlot(contractId, data) {
    const response = await apiClient.post(`/contracts/${contractId}/slots`, data);
    return response.data;
  }

  /**
   * Update a slot
   * PUT /contracts/{contractId}/slots/{id}
   */
  async updateSlot(contractId, id, data) {
    const response = await apiClient.put(`/contracts/${contractId}/slots/${id}`, data);
    return response.data;
  }

  /**
   * Delete a slot
   * DELETE /contracts/{contractId}/slots/{id}
   */
  async deleteSlot(contractId, id) {
    const response = await apiClient.delete(`/contracts/${contractId}/slots/${id}`);
    return response.data;
  }

  // ==================== Pause Endpoints (Part 3) ====================

  /**
   * Get pauses for a contract
   * GET /contracts/{contractId}/pauses
   */
  async getPauses(contractId) {
    const response = await apiClient.get(`/contracts/${contractId}/pauses`);
    return response.data;
  }

  /**
   * Create a pause
   * POST /contracts/{contractId}/pauses
   */
  async createPause(contractId, data) {
    const response = await apiClient.post(`/contracts/${contractId}/pauses`, data);
    return response.data;
  }

  /**
   * Update a pause
   * PUT /contracts/{contractId}/pauses/{id}
   */
  async updatePause(contractId, id, data) {
    const response = await apiClient.put(`/contracts/${contractId}/pauses/${id}`, data);
    return response.data;
  }

  /**
   * Delete a pause
   * DELETE /contracts/{contractId}/pauses/{id}
   */
  async deletePause(contractId, id) {
    const response = await apiClient.delete(`/contracts/${contractId}/pauses/${id}`);
    return response.data;
  }

  // ==================== Session Endpoints (Part 3) ====================

  /**
   * Get sessions for a contract
   * GET /contracts/{contractId}/sessions
   */
  async getSessions(contractId, params = {}) {
    const response = await apiClient.get(`/contracts/${contractId}/sessions`, { params });
    return response.data;
  }

  /**
   * Get a single session
   * GET /contracts/{contractId}/sessions/{id}
   */
  async getSession(contractId, id) {
    const response = await apiClient.get(`/contracts/${contractId}/sessions/${id}`);
    return response.data;
  }

  /**
   * Start a session
   * POST /contracts/{contractId}/sessions/{id}/start
   */
  async startSession(contractId, id) {
    const response = await apiClient.post(`/contracts/${contractId}/sessions/${id}/start`);
    return response.data;
  }

  /**
   * End a session
   * POST /contracts/{contractId}/sessions/{id}/end
   */
  async endSession(contractId, id) {
    const response = await apiClient.post(`/contracts/${contractId}/sessions/${id}/end`);
    return response.data;
  }

  /**
   * Report a session (compte rendu → réalisé)
   * POST /contracts/{contractId}/sessions/{id}/report
   */
  async reportSession(contractId, id, data) {
    const response = await apiClient.post(`/contracts/${contractId}/sessions/${id}/report`, data);
    return response.data;
  }

  /**
   * Cancel a session
   * POST /contracts/{contractId}/sessions/{id}/cancel
   */
  async cancelSession(contractId, id, data) {
    const response = await apiClient.post(`/contracts/${contractId}/sessions/${id}/cancel`, data);
    return response.data;
  }

  // ==================== Tutor Endpoints (Part 4) ====================

  /**
   * Get list of tutors (admin sees all; filtered client-side for pending_verification queue)
   * GET /tutors
   */
  async getTutors(params = {}) {
    const response = await apiClient.get('/tutors', { params });
    return response.data;
  }

  /**
   * Get a single tutor profile
   * GET /tutors/{id}
   */
  async getTutor(id) {
    const response = await apiClient.get(`/tutors/${id}`);
    return response.data;
  }

  /**
   * Verify (approve) a tutor
   * POST /tutors/{id}/verify
   */
  async verifyTutor(id, data = {}) {
    const response = await apiClient.post(`/tutors/${id}/verify`, data);
    return response.data;
  }

  /**
   * Reject a tutor (mandatory reason)
   * POST /tutors/{id}/reject
   */
  async rejectTutor(id, data) {
    const response = await apiClient.post(`/tutors/${id}/reject`, data);
    return response.data;
  }

  /**
   * Suspend a tutor
   * POST /tutors/{id}/suspend
   */
  async suspendTutor(id, data) {
    const response = await apiClient.post(`/tutors/${id}/suspend`, data);
    return response.data;
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

// Also export the axios instance for advanced use cases
export { apiClient };
